// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Activity Log Types
 *
 * Unified types for the Activity Log page that merges
 * dashboard alerts and insights into a single feed.
 */

import type { AlertSeverity, AlertType, AlertAction, DashboardAlert } from './dashboard';
import type { DashboardInsight } from '../components/dashboard/DashboardAlertsPanel';

// ============================================
// Unified Activity Item
// ============================================

export type ActivitySource = 'alert' | 'insight';

export interface UnifiedActivityItem {
  id: string;
  source: ActivitySource;
  title: string;
  description: string;
  timestamp: string;
  severity?: AlertSeverity;
  alertType?: AlertType;
  profileId?: string;
  resourceId?: string;
  resourceType?: string;
  actions?: AlertAction[];
}

// ============================================
// Filter Types
// ============================================

export type ActivityTypeFilter = 'all' | 'urgent' | 'warning' | 'info' | 'insights';

export interface ActivityLogFilters {
  typeFilter: ActivityTypeFilter;
  startDate?: string;
  endDate?: string;
}

// ============================================
// Merge Utility
// ============================================

export function mergeAlertsAndInsights(
  alerts: DashboardAlert[],
  insights: DashboardInsight[]
): UnifiedActivityItem[] {
  const alertItems: UnifiedActivityItem[] = alerts.map((alert) => ({
    id: alert.id,
    source: 'alert' as const,
    title: alert.title,
    description: alert.description,
    timestamp: alert.eventTimestamp,
    severity: alert.severity,
    alertType: alert.type,
    profileId: alert.profileId,
    resourceId: alert.resourceId,
    resourceType: alert.resourceType,
    actions: alert.actions,
  }));

  const insightItems: UnifiedActivityItem[] = insights.map((insight) => ({
    id: insight.id,
    source: 'insight' as const,
    title: insight.title,
    description: insight.description,
    timestamp: insight.timestamp,
  }));

  return [...alertItems, ...insightItems].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

// ============================================
// Client-side Filtering
// ============================================

export function filterActivityItems(
  items: UnifiedActivityItem[],
  filters: ActivityLogFilters
): UnifiedActivityItem[] {
  let filtered = items;

  if (filters.typeFilter !== 'all') {
    if (filters.typeFilter === 'insights') {
      filtered = filtered.filter((item) => item.source === 'insight');
    } else {
      const severityMap: Record<string, AlertSeverity> = {
        urgent: 'URGENT',
        warning: 'WARNING',
        info: 'INFO',
      };
      const severity = severityMap[filters.typeFilter];
      filtered = filtered.filter(
        (item) => item.source === 'alert' && item.severity === severity
      );
    }
  }

  if (filters.startDate) {
    const start = new Date(filters.startDate);
    start.setHours(0, 0, 0, 0);
    filtered = filtered.filter((item) => new Date(item.timestamp) >= start);
  }

  if (filters.endDate) {
    const end = new Date(filters.endDate);
    end.setHours(23, 59, 59, 999);
    filtered = filtered.filter((item) => new Date(item.timestamp) <= end);
  }

  return filtered;
}
